import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Combobox,
  TextInput,
  useCombobox,
  CloseButton,
  Text,
  Stack,
  Group,
  Button,
  Box,
  ScrollArea,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import Fuse, { type IFuseOptions } from "fuse.js";

import { useAppEnvironment, useAppEssentialsContext } from "@/contexts";
import { ExpenseKind, ExpenseCategory } from "@/utils/api";
import { AppIcon } from "@/components/generic";
import { ExpenseKindPickerModal } from "./ExpenseKindPickerModal";

// ============================================
// Types
// ============================================

export interface ExpenseKindSelectorProps {
  /** Field name for form binding */
  name?: string;

  /** Currently selected kind ID */
  value: number | null;

  /** Callback when selection changes: (name, kindId) */
  onChange: (name: string, value: number | null) => void;

  /** Input label */
  label?: string;

  /** Placeholder text */
  placeholder?: string;

  /** Input size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Error message */
  error?: string | null;

  /** Whether field is required */
  required?: boolean;

  /** Whether field is disabled */
  disabled?: boolean;

  /** Quick access expense codes to show as buttons */
  quickAccessCodes?: string[];

  schedulableOnly?: boolean;
}

// ============================================
// Constants
// ============================================

// Default quick access buttons - common expense types
const DEFAULT_QUICK_ACCESS_CODES = ["PARKING", "CAR_WASH_EXTERIOR", "TOLLS"];

// Icon mapping for expense categories/kinds
const CATEGORY_ICONS: Record<string, string> = {
  MAINTENANCE: "CarGarage",
  REPAIRS: "Tool",
  OTHER: "Dots",
};

const KIND_ICONS: Record<string, string> = {
  parking: "Parking",
  car_wash: "Droplet",
  toll: "Road",
  oil_change: "Oil",
  tire_rotation: "Circle",
  insurance: "Shield",
  registration: "FileText",
  inspection: "ClipboardCheck",
};

// Size configuration for consistent scaling
const ICON_SIZES = {
  inputLeft: 20,
  inputRight: 18,
  categoryHeader: 18,
  kindOption: 20,
  maintenanceBadge: 16,
  quickAccess: 16,
};

// Fuse.js configuration for fuzzy search
const FUSE_OPTIONS_KINDS: IFuseOptions<ExpenseKind> = {
  keys: [
    { name: "name", weight: 0.7 },
    { name: "code", weight: 0.3 },
  ],
  threshold: 0.35,
  distance: 100,
  minMatchCharLength: 2,
  ignoreLocation: true,
};

const FUSE_OPTIONS_CATEGORIES: IFuseOptions<ExpenseCategory> = {
  keys: [{ name: "name", weight: 1.0 }],
  threshold: 0.35,
  distance: 100,
  minMatchCharLength: 2,
  ignoreLocation: true,
};

// ============================================
// Helper Functions
// ============================================

const getIconForKind = (code: string | undefined): string => {
  if (!code) return "Receipt";
  return KIND_ICONS[code.toLowerCase()] || "Receipt";
};

const getIconForCategory = (code: string | undefined): string => {
  if (!code) return "Folder";
  return CATEGORY_ICONS[code.toUpperCase()] || "Folder";
};

// ============================================
// Component
// ============================================

export const ExpenseKindSelector: React.FC<ExpenseKindSelectorProps> = ({
  name = "kindId",
  value,
  onChange,
  label,
  placeholder,
  size = "md",
  error,
  required,
  disabled,
  quickAccessCodes = DEFAULT_QUICK_ACCESS_CODES,
  schedulableOnly,
}) => {
  const { t } = useTranslation(["expenses", "common"]);
  const { expenseCategories, expenseKinds, getExpenseKindById } =
    useAppEssentialsContext();
  const { isMobile } = useAppEnvironment();

  const [modalOpened, setModalOpened] = useState(false);

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearchValue("");
    },
  });

  const [searchValue, setSearchValue] = useState("");

  // Get the selected kind object
  const selectedKind = useMemo(() => {
    if (value === null || value === undefined) return null;
    return getExpenseKindById(value) || null;
  }, [value, getExpenseKindById]);

  // Get quick access kinds
  const quickAccessKinds = useMemo(() => {
    return quickAccessCodes
      .map((code) =>
        expenseKinds.find((k) => k.code?.toUpperCase() === code.toUpperCase())
      )
      .filter((k): k is ExpenseKind => k !== undefined);
  }, [quickAccessCodes, expenseKinds]);

  // Auto-select first kind when required and no value is set
  useEffect(() => {
    if (
      required &&
      (value === null || value === undefined) &&
      expenseCategories.length > 0
    ) {
      for (const category of expenseCategories) {
        if (category.kinds && category.kinds.length > 0) {
          const firstKind = category.kinds[0];
          if (firstKind?.id !== undefined) {
            onChange(name, Number(firstKind.id));
            break;
          }
        }
      }
    }
  }, [required, value, expenseCategories, onChange, name]);

  // Build Fuse indexes for fuzzy search (memoized per data change)
  const categoryFuse = useMemo(
    () => new Fuse(expenseCategories, FUSE_OPTIONS_CATEGORIES),
    [expenseCategories]
  );

  const kindFuseByCategory = useMemo(() => {
    const map = new Map<string | number, Fuse<ExpenseKind>>();
    for (const category of expenseCategories) {
      if (category.kinds && category.kinds.length > 0) {
        map.set(
          category.id || "",
          new Fuse(category.kinds, FUSE_OPTIONS_KINDS)
        );
      }
    }
    return map;
  }, [expenseCategories]);

  // Filter categories and kinds based on fuzzy search
  const filteredCategories = useMemo(() => {
    const search = searchValue.trim();

    if (!search) {
      return expenseCategories;
    }

    // Find categories whose name fuzzy-matches
    const matchedCategoryIds = new Set(
      categoryFuse.search(search).map((result) => result.item.id)
    );

    return expenseCategories
      .map((category) => {
        // If category name fuzzy-matches, include all its kinds
        if (matchedCategoryIds.has(category.id)) {
          return category;
        }

        // Otherwise, fuzzy-search within this category's kinds
        const fuse = kindFuseByCategory.get(category.id || "");
        if (!fuse) return null;

        const matchingKinds = fuse.search(search).map((result) => result.item);

        if (matchingKinds.length > 0) {
          return { ...category, kinds: matchingKinds };
        }

        return null;
      })
      .filter((c): c is ExpenseCategory => c !== null);
  }, [expenseCategories, searchValue, categoryFuse, kindFuseByCategory]);

  // Handle selection (desktop combobox)
  const handleSelect = useCallback(
    (kindId: string) => {
      const numericId = parseInt(kindId, 10);
      onChange(name, isNaN(numericId) ? null : numericId);
      combobox.closeDropdown();
    },
    [name, onChange, combobox]
  );

  // Handle quick access button click
  const handleQuickSelect = useCallback(
    (kind: ExpenseKind) => {
      if (kind.id !== undefined) {
        onChange(name, Number(kind.id));
      }
    },
    [name, onChange]
  );

  // Handle clear selection
  const handleClear = useCallback(() => {
    onChange(name, null);
    setSearchValue("");
  }, [name, onChange]);

  // Handle clearing search text (without deselecting the kind)
  const handleClearSearch = useCallback(() => {
    setSearchValue("");
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.currentTarget.value);
      combobox.updateSelectedOptionIndex();
    },
    [combobox]
  );

  // Handle modal selection
  const handleModalSelect = useCallback(
    (kindId: number | null) => {
      onChange(name, kindId);
    },
    [name, onChange]
  );

  // Display value in input
  const displayValue = useMemo(() => {
    if (!isMobile && combobox.dropdownOpened) {
      return searchValue;
    }
    return selectedKind?.name || "";
  }, [isMobile, combobox.dropdownOpened, searchValue, selectedKind]);

  // Default label and placeholder
  const fieldLabel = label ?? t("expenses:form.fields.expenseKind.label");
  const fieldPlaceholder =
    placeholder ?? t("expenses:form.fields.expenseKind.placeholder");

  // Right section content (desktop only — mobile uses a simpler approach)
  const getRightSection = () => {
    // When dropdown is open and there's search text, show clear-search button
    if (combobox.dropdownOpened && searchValue) {
      return (
        <CloseButton
          size={size}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClearSearch}
          aria-label={t("common:form.clearSearch")}
        />
      );
    }

    // When dropdown is closed and a kind is selected, show clear-selection button
    // Only allow clearing when field is not required
    if (selectedKind && !required && !disabled && !combobox.dropdownOpened) {
      return (
        <CloseButton
          size={size}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClear}
          aria-label={t("common:form.clearSelection")}
        />
      );
    }

    return <AppIcon iconName="ChevronDown" size={ICON_SIZES.inputRight} />;
  };

  // Right section for mobile trigger input
  const getMobileRightSection = () => {
    if (selectedKind && !required && !disabled) {
      return (
        <CloseButton
          size={size}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            handleClear();
          }}
          aria-label={t("common:form.clearSelection")}
        />
      );
    }

    return <AppIcon iconName="ChevronDown" size={ICON_SIZES.inputRight} />;
  };

  // Check if there are any results
  const hasResults = filteredCategories.some(
    (c) => c.kinds && c.kinds.length > 0
  );

  // ==========================================
  // Mobile: Read-only input + full-screen modal
  // ==========================================
  if (isMobile) {
    return (
      <Stack gap="xs">
        {/* Trigger Input — read-only, opens modal on tap */}
        <TextInput
          label={fieldLabel}
          placeholder={fieldPlaceholder}
          value={selectedKind?.name || ""}
          readOnly
          onClick={() => {
            if (!disabled) {
              setModalOpened(true);
            }
          }}
          rightSection={getMobileRightSection()}
          rightSectionPointerEvents={
            selectedKind && !required && !disabled ? "auto" : "none"
          }
          error={error}
          required={required}
          disabled={disabled}
          size={size}
          leftSection={
            selectedKind ? (
              <AppIcon
                iconName={getIconForKind(selectedKind.code)}
                size={ICON_SIZES.inputLeft}
              />
            ) : undefined
          }
          styles={{
            input: {
              cursor: disabled ? "not-allowed" : "pointer",
              caretColor: "transparent",
            },
          }}
        />

        {/* Quick Access Buttons */}
        {quickAccessKinds.length > 0 && !disabled && (
          <Group gap="xs" wrap="wrap">
            {quickAccessKinds.map((kind) => (
              <Button
                key={kind.id}
                variant={value === kind.id ? "filled" : "light"}
                size="sm"
                leftSection={
                  <AppIcon
                    iconName={getIconForKind(kind.code)}
                    size={ICON_SIZES.quickAccess}
                  />
                }
                onClick={() => handleQuickSelect(kind)}
              >
                {kind.name}
              </Button>
            ))}
          </Group>
        )}

        {/* Full-screen Picker Modal */}
        <ExpenseKindPickerModal
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
          value={value}
          onSelect={handleModalSelect}
          quickAccessCodes={quickAccessCodes}
          schedulableOnly={schedulableOnly}
        />
      </Stack>
    );
  }

  // ==========================================
  // Desktop: Original Combobox behavior
  // ==========================================
  return (
    <Stack gap="xs">
      {/* Combobox Selector */}
      <Combobox store={combobox} onOptionSubmit={handleSelect} withinPortal>
        <Combobox.Target>
          <TextInput
            label={fieldLabel}
            placeholder={fieldPlaceholder}
            value={displayValue}
            onChange={handleSearchChange}
            onClick={() => combobox.openDropdown()}
            onFocus={() => combobox.openDropdown()}
            onBlur={() => {
              combobox.closeDropdown();
              setSearchValue("");
            }}
            rightSection={getRightSection()}
            rightSectionPointerEvents={
              (combobox.dropdownOpened && searchValue) ||
                (selectedKind && !required)
                ? "auto"
                : "none"
            }
            error={error}
            required={required}
            disabled={disabled}
            size={size}
            leftSection={
              selectedKind ? (
                <AppIcon
                  iconName={getIconForKind(selectedKind.code)}
                  size={ICON_SIZES.inputLeft}
                />
              ) : undefined
            }
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <ScrollArea.Autosize mah={400} type="scroll">
            <Combobox.Options>
              {/* No results message */}
              {!hasResults && searchValue && (
                <Combobox.Empty>
                  {t("expenses:form.fields.expenseKind.noResults")}
                </Combobox.Empty>
              )}

              {/* Grouped options by category */}
              {filteredCategories.map((category) => {
                if (!category.kinds || category.kinds.length === 0) {
                  return null;
                }

                const categoryKinds = schedulableOnly
                  ? category.kinds //TODO: .filter(kind => kind.canSchedule)
                  : category.kinds;

                return (
                  <Combobox.Group
                    key={category.id}
                    label={
                      <Group gap="xs" py={4}>
                        <AppIcon
                          iconName={getIconForCategory(category.code)}
                          size={ICON_SIZES.categoryHeader}
                        />
                        <Text size="md" fw={600} tt="uppercase">
                          {category.name}
                        </Text>
                      </Group>
                    }
                  >
                    {categoryKinds.map((kind) => (
                      <Combobox.Option
                        key={kind.id}
                        value={String(kind.id)}
                        active={value === kind.id}
                        py="sm"
                      >
                        <Group gap="sm" wrap="nowrap">
                          <AppIcon
                            iconName={getIconForKind(kind.code)}
                            size={ICON_SIZES.kindOption}
                          />
                          <Text size="md">{kind.name}</Text>
                          {kind.isItMaintenance && (
                            <Box ml="auto">
                              <AppIcon
                                iconName="Tool"
                                size={ICON_SIZES.maintenanceBadge}
                                color="dimmed"
                              />
                            </Box>
                          )}
                        </Group>
                      </Combobox.Option>
                    ))}
                  </Combobox.Group>
                );
              })}
            </Combobox.Options>
          </ScrollArea.Autosize>
        </Combobox.Dropdown>
      </Combobox>

      {/* Quick Access Buttons */}
      {quickAccessKinds.length > 0 && !disabled && (
        <Group gap="xs" wrap="wrap">
          {quickAccessKinds.map((kind) => (
            <Button
              key={kind.id}
              variant={value === kind.id ? "filled" : "light"}
              size="sm"
              leftSection={
                <AppIcon
                  iconName={getIconForKind(kind.code)}
                  size={ICON_SIZES.quickAccess}
                />
              }
              onClick={() => handleQuickSelect(kind)}
            >
              {kind.name}
            </Button>
          ))}
        </Group>
      )}
    </Stack>
  );
};